import { asyncHandler } from '../async-handler';
import store from '../../../store/store';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import { DELETE_SHOP_API } from '../../constants/central-api';

export interface DeleteShopResponse {
  success: boolean;
  message?: string;
}

/**
 * Delete a brand (shop). Hits `DELETE /api/v1/shops/{slug}`, which is a SOFT
 * delete server-side (`Shop` uses SoftDeletes) — the row and every dependent
 * record (credentials, MCP connections, chats) stay in place, just hidden from
 * seller-init, so support can restore a brand deleted by mistake.
 *
 * The endpoint answers 204 No Content on success, which `asyncHandler` returns
 * as `{ data: null, responseStatus: 204 }` — hence the status check below.
 */
export async function deleteShop(
  shopSlug: string
): Promise<DeleteShopResponse> {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const isWhiteLabel = state.UI.isWhiteLabel;

  const requestInfo: RequestInfo = {
    url: DELETE_SHOP_API(shopSlug),
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: `${accessToken.type} ${
        isWhiteLabel
          ? accessToken.currentWhiteLabelSellerToken
          : accessToken.sellerToken
      }`,
    },
  };

  const res: any = await asyncHandler(
    requestInfo,
    'DELETE_SHOP',
    'Brand deleted.',
    false,
    false,
    true
  );

  if (res?.responseStatus === 204 || res?.data?.success === true) {
    return { success: true };
  }

  const response = res?.data;
  const errorMessage =
    response?.message ||
    (Array.isArray(response?.errors)
      ? response.errors.join(' ')
      : typeof response?.errors === 'object' && response?.errors !== null
      ? Object.values(response.errors).join(' ')
      : '') ||
    'Failed to delete brand.';

  return {
    success: false,
    message: errorMessage,
  };
}
